import { useEffect } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { signalRService } from '../services/signalRService';
import { queryKeys } from '../config/queryKeys';
import { Appointment } from '../services/appointmentService';

/**
 * Hook to sync SignalR appointment events with TanStack Query cache.
 * Call this once at the app level (e.g., in MainLayout or after login).
 */
export function useSignalRSync(businessId: string | undefined) {
  const queryClient = useQueryClient();

  useEffect(() => {
    if (!businessId) return;

    // Handle appointment created
    const unsubscribeCreated = signalRService.onAppointmentCreated(
      (appointment: Appointment) => {
        console.log('SignalR: Appointment created, invalidating queries');

        // Invalidate appointment lists to refetch with new data
        queryClient.invalidateQueries({
          queryKey: queryKeys.appointments.lists(),
        });

        // Invalidate recent activity
        queryClient.invalidateQueries({
          queryKey: queryKeys.appointments.recentActivity(),
        });

        // If this appointment is for a specific customer or job, invalidate those queries too
        if (appointment.customerId) {
          queryClient.invalidateQueries({
            queryKey: queryKeys.appointments.byCustomer(appointment.customerId),
          });
        }
        if (appointment.jobId) {
          queryClient.invalidateQueries({
            queryKey: queryKeys.appointments.byJob(appointment.jobId),
          });
        }
      }
    );

    // Handle appointment updated
    const unsubscribeUpdated = signalRService.onAppointmentUpdated(
      (appointment: Appointment) => {
        console.log('SignalR: Appointment updated, updating cache');

        // Update the specific appointment detail in cache
        queryClient.setQueryData(
          queryKeys.appointments.detail(appointment.id),
          appointment
        );

        // Invalidate lists to refresh them
        queryClient.invalidateQueries({
          queryKey: queryKeys.appointments.lists(),
        });

        // Invalidate recent activity
        queryClient.invalidateQueries({
          queryKey: queryKeys.appointments.recentActivity(),
        });

        // If this appointment is for a specific customer or job, invalidate those queries too
        if (appointment.customerId) {
          queryClient.invalidateQueries({
            queryKey: queryKeys.appointments.byCustomer(appointment.customerId),
          });
        }
        if (appointment.jobId) {
          queryClient.invalidateQueries({
            queryKey: queryKeys.appointments.byJob(appointment.jobId),
          });
        }
      }
    );

    // Handle appointment deleted
    const unsubscribeDeleted = signalRService.onAppointmentDeleted(
      (appointmentId: string) => {
        console.log('SignalR: Appointment deleted, removing from cache');

        // Remove from detail cache
        queryClient.removeQueries({
          queryKey: queryKeys.appointments.detail(appointmentId),
        });

        // Invalidate lists
        queryClient.invalidateQueries({
          queryKey: queryKeys.appointments.lists(),
        });

        // Invalidate recent activity
        queryClient.invalidateQueries({
          queryKey: queryKeys.appointments.recentActivity(),
        });
      }
    );

    // Handle recurring instance deleted
    const unsubscribeRecurringDeleted = signalRService.onRecurringInstanceDeleted(
      ({ recurringAppointmentId, instanceDate }) => {
        console.log('SignalR: Recurring instance deleted, invalidating queries');

        queryClient.invalidateQueries({
          queryKey: queryKeys.appointments.lists(),
        });

        queryClient.invalidateQueries({
          queryKey: queryKeys.appointments.recentActivity(),
        });
      }
    );

    // Handle recurring series deleted
    const unsubscribeRecurringSeriesDeleted = signalRService.onRecurringSeriesDeleted(
      ({ recurringAppointmentId, deleteOption }) => {
        console.log('SignalR: Recurring series deleted, invalidating queries');

        // Remove the series from detail cache
        queryClient.removeQueries({
          queryKey: queryKeys.appointments.detail(recurringAppointmentId),
        });

        queryClient.invalidateQueries({
          queryKey: queryKeys.appointments.lists(),
        });

        queryClient.invalidateQueries({
          queryKey: queryKeys.appointments.recentActivity(),
        });
      }
    );

    return () => {
      unsubscribeCreated();
      unsubscribeUpdated();
      unsubscribeDeleted();
      unsubscribeRecurringDeleted();
      unsubscribeRecurringSeriesDeleted();
    };
  }, [businessId, queryClient]);
}
